import { Calendar, Eye, EyeOff, Loader2 } from "lucide-react";
import { forwardRef, useEffect, useState } from "react";

const InputField = forwardRef(
  (
    {
      label,
      type = "text",
      name,
      value,
      onChange,
      onFocus,
      onBlur,
      defaultValue,
      placeholder = "",
      className = "",
      error = false,
      required = false,
      disabled = false,
      readOnly = false,
      minLength,
      maxLength,
      rows = 4,
      Icon,
      IconRight,
      image,
      validatePassword = false,
      id,
      checked,
      loading = false,
      onKeyDown,
      onPasswordValidate
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [internalValue, setInternalValue] = useState(
      defaultValue || value || "",
    );

    const isPassword = type === "password";
    const isTextarea = type === "textarea";
    const isSwitch = type === "switch";
    const isCheckbox = type === "checkbox";
    const inputType = isPassword && showPassword ? "text" : type;

    const toggleVisibility = () => setShowPassword((prev) => !prev);


    // Update internal value when defaultValue or value changes
    useEffect(() => {
      if (defaultValue !== undefined) {
        setInternalValue(defaultValue);
      } else if (value !== undefined) {
        setInternalValue(value);
      }
    }, [defaultValue, value]);


    useEffect(() => {
      if (validatePassword && isPassword && onPasswordValidate) {
        // Check all validation conditions
        const isValid =
          isValidLength(internalValue) &&
          isUppercaseLetterPresent(internalValue) &&
          isNumberPresent(internalValue) &&
          isSpecialCharacterPresent(internalValue);

        onPasswordValidate(isValid);
      }
    }, [internalValue, validatePassword, isPassword, onPasswordValidate]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      if (onChange) {
        onChange(e);
      }

      // Also validate password on change if validatePassword is enabled
      if (validatePassword && isPassword && onPasswordValidate) {
        const isValid =
          isValidLength(newValue) &&
          isUppercaseLetterPresent(newValue) &&
          isNumberPresent(newValue) &&
          isSpecialCharacterPresent(newValue);
        onPasswordValidate(isValid);
      }
    };

    const handleKeyDown = (e) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Password validation helpers
    const isUppercaseLetterPresent = (text = "") => /[A-Z]/.test(text);
    const isNumberPresent = (text = "") => /\d/.test(text);
    const isSpecialCharacterPresent = (text = "") =>
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(text);
    const isValidLength = (text = "") => text.length >= 10 && text.length <= 18;

    return (
      <div className={`icon-form-group ${className}`}>
        {label && !isCheckbox && (
          <label htmlFor={id || name}>
            {label} {required && <span className="text-danger">*</span>}
          </label>
        )}

        <div className="icon-input">
          {Icon && <span className="icon left">{Icon}</span>}

          {loading ? (
            <span className="icon right">
              <Loader2 className="spin" size={18} />
            </span>
          ) : (
            <>
              {IconRight && <span className="icon right">{IconRight}</span>}
              {type === "date" && (
                <span className="icon right" style={{ pointerEvents: "none" }}>
                  <Calendar />
                </span>
              )}
              {isPassword && (
                <span
                  className="icon right"
                  onClick={toggleVisibility}
                  style={{ cursor: "pointer" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </span>
              )}
            </>
          )}

          {image && (
            <span className="icon left">
              <img src={image} alt="" />
            </span>
          )}

          {isSwitch ? (
            <label className="switch">
              <input
                ref={ref}
                type="checkbox"
                id={id || name}
                name={name}
                checked={checked ?? false}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={disabled}
                readOnly={readOnly}
              />
              <span className="slider round"></span>
            </label>
          ) : isCheckbox ? (
            <div className="checkbox-wrap">
              <div className="checkbox">
                <input
                  ref={ref}
                  type="checkbox"
                  id={id || name}
                  name={name}
                  checked={checked ?? false}
                  onChange={onChange}
                  disabled={disabled}
                  readOnly={readOnly}
                />
                <span className="checkmark"></span>
              </div>
              {label && (
                <label id={id || name} className="checkbox-label">
                  {label}
                </label>
              )}
            </div>
          ) : !isTextarea ? (
            <input
              ref={ref}
              type={inputType}
              name={name}
              id={id || name}
              value={internalValue}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={disabled || loading}
              readOnly={readOnly}
              required={required}
              minLength={minLength}
              maxLength={maxLength}
              className={`form-control ${error ? "is-invalid" : ""} ${disabled ? "disabled" : ""
                }`}
            />
          ) : (
            <textarea
              ref={ref}
              name={name}
              id={id || name}
              value={internalValue}
              placeholder={placeholder}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={disabled || loading}
              readOnly={readOnly}
              required={required}
              minLength={minLength}
              maxLength={maxLength}
              rows={rows}
              className={`form-control ${error ? "is-invalid" : ""} ${disabled ? "disabled" : ""
                }`}
            />
          )}
        </div>

        {error && <span className="error">{error}</span>}

        {validatePassword && isPassword && (
          <ul className="password-validation-message">
            <li
              className={
                isValidLength(internalValue) ? "completed" : "uncompleted"
              }
            >
              Minimum 10 to 18 characters
            </li>
            <li
              className={
                isUppercaseLetterPresent(internalValue)
                  ? "completed"
                  : "uncompleted"
              }
            >
              At least one uppercase letter
            </li>
            <li
              className={
                isNumberPresent(internalValue) ? "completed" : "uncompleted"
              }
            >
              At least one number
            </li>
            <li
              className={
                isSpecialCharacterPresent(internalValue)
                  ? "completed"
                  : "uncompleted"
              }
            >
              At least one special character
            </li>
          </ul>
        )}
      </div>
    );
  },
);

export default InputField;
